import { useState, useEffect, useRef } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth, api } from '../contexts/AuthContext';
import { useOnlineStatus } from '../lib/useOnlineStatus';
import { Button } from './ui/button';
import { ScrollArea } from './ui/scroll-area';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from './ui/dropdown-menu';
import { Separator } from './ui/separator';
import {
  LayoutDashboard, Building2, Package, Warehouse, ShoppingCart,
  Users, Tags, Receipt, Calculator, Settings, Menu, X,
  ChevronDown, LogOut, User, Store, Truck, Shield, ClipboardList, UserCog, Briefcase, Upload, Lock, ArrowRight, BarChart3, RotateCcw, ShieldCheck, WifiOff, FileText, AlertTriangle, HardDrive, ScanBarcode, Search, BookOpen, FolderOpen, MessageSquare, Smartphone, Sprout, TrendingUp, Printer, GitMerge, FileCheck, Zap, Eye, EyeOff
, Scale, FilePenLine } from 'lucide-react';
import OfflineIndicator from './OfflineIndicator';
import NotificationBell from './NotificationBell';
import QuickSearch from './QuickSearch';
import SectionOverrideDialog from './SectionOverrideDialog';
import ActiveShiftBadge from './shifts/ActiveShiftBadge';
import DemoBanner from './DemoBanner';
import FeaturePreviewModal from './FeaturePreviewModal';
import OnboardingWizard from './OnboardingWizard';
import BranchTransferAlertBubble from './BranchTransferAlertBubble';
import { toast } from 'sonner';

// ── Feature upgrade hints for the ghost-mode upgrade teaser ──────────────────
const FEATURE_UPGRADE_MAP = {
  purchase_orders:        { plan: 'Business',     color: '#477242', features: ['Create POs and auto-update stock on receive', 'Track outstanding payables per supplier', 'Full purchase history and cost analysis'] },
  supplier_management:    { plan: 'Business',     color: '#477242', features: ['Centralized supplier database', 'Payment terms + contact tracking', 'Per-supplier purchase history'] },
  pay_supplier:           { plan: 'Business',     color: '#477242', features: ['Record supplier payments from any wallet', 'Auto-reduce accounts payable', 'Payment history per supplier'] },
  full_fund_management:   { plan: 'Business',     color: '#477242', features: ['4 separate wallets: Cashier, Safe, Digital, Bank', 'Mid-day fund transfers with manager auth', 'Full wallet movement audit trail'] },
  employee_management:    { plan: 'Business',     color: '#477242', features: ['Employee profiles + cash advance tracking', 'Monthly advance limits with manager approval', 'Expense attribution per employee'] },
  multi_cashier_shifts:   { plan: 'Business',     color: '#477242', features: ['Open simultaneous cashier shifts', 'Per-cashier over/short tracking', 'Dual-signature close with manager PIN'] },
  advanced_reports:       { plan: 'Business',     color: '#477242', features: ['Income statements + AR aging reports', 'Inventory movement by date range', 'Cash flow and expense breakdowns'] },
  z_report_pdf:           { plan: 'Business',     color: '#477242', features: ['Download Z-Report as PDF', 'Normal + Detailed modes', 'Auto-appended BIR Activity section'] },
  sms_gateway:            { plan: 'Business',     color: '#477242', features: ['Unlimited SMS via your own SIM card', 'Auto-notify customers on sale/payment/overdue', '12 customizable message templates'] },
  telegram_notifications: { plan: 'Business',     color: '#477242', features: ['Real-time alerts on every major event', 'Sale summaries, low stock, cash variances', 'Multi-chat group support'] },
  branch_transfers:       { plan: 'Professional', color: '#C85A32', features: ['Transfer stock between branches instantly', 'Cost tracking + destination repack pricing', 'Transfer approval workflow'] },
  multi_branch:           { plan: 'Professional', color: '#C85A32', features: ['Manage multiple store branches', 'Consolidated owner dashboard view', 'Per-branch inventory + reporting'] },
  bir_compliance:         { plan: 'Professional', color: '#C85A32', features: ['Issue official BIR Receipts (OR/SI)', 'Sequential INV-MB-XXXXXX numbering', 'VAT computation (12% back-calc)'] },
  audit_center:           { plan: 'Professional', color: '#C85A32', features: ['Transaction verification with manager PIN', 'Discrepancy detection and scoring', 'Full audit timeline with filter/export'] },
  incident_tickets:       { plan: 'Professional', color: '#C85A32', features: ['Log and track operational incidents', 'Assign resolution owners + status tracking', 'Integrated with cashier accountability'] },
  count_sheets:           { plan: 'Business',     color: '#477242', features: ['Physical inventory count sheets', 'Variance detection vs system stock', 'Manager sign-off workflow'] },
  granular_permissions:   { plan: 'Professional', color: '#C85A32', features: ['Custom permissions per user per module', 'Restrict specific actions (edit price, void, etc.)', 'Role templates for fast setup'] },
  transaction_verification:{ plan: 'Enterprise',  color: '#2D3728', features: ['Real-time transaction drift detection', 'Automated reconciliation reports', 'Audit scoring system'] },
  two_fa:                 { plan: 'Enterprise',   color: '#2D3728', features: ['TOTP / Google Authenticator 2FA', 'Admin account protection', 'Backup recovery codes'] },
};

// offlineOk: 'full' = works offline | 'readonly' = view cached only | false/omit = requires internet
// featureFlag: subscription feature key required to access this nav item (null = always shown)
const NAV_SECTIONS = [
  {
    label: null,
    items: [
      { path: '/dashboard', label: 'Dashboard', icon: LayoutDashboard, perm: null, offlineOk: 'readonly' },
      { path: '/find-transaction', label: 'Find Transaction', icon: Search, perm: null },
      { path: '/messages', label: 'SMS Messages', icon: MessageSquare, perm: null },
    ],
  },
  {
    label: 'Transactions',
    items: [
      { path: '/sales-new',  label: 'Sales',             icon: ShoppingCart, perm: 'sales.view',       offlineOk: 'full' },
      { path: '/sales-history', label: 'Sales History',  icon: ClipboardList, perm: 'sales.view' },
      { path: '/returns',    label: 'Return & Refund',   icon: RotateCcw,    perm: 'sales.process_return' },
      { path: '/customers',  label: 'Customers',         icon: Users,        perm: 'customers.view',   offlineOk: 'readonly' },
      { path: '/payments',   label: 'Receive Payments',  icon: Tags,         perm: 'accounting.view' },
      { path: '/check-monitoring', label: 'Check Monitoring', icon: FileCheck, perm: 'accounting.view' },
      { path: '/crop-credits', label: 'Crop Credits',    icon: Sprout,       perm: 'customers.manage_credit' },
      { path: '/expenses',   label: 'Expenses',          icon: Receipt,      perm: 'accounting.view' },
    ],
  },
  {
    label: 'Inventory & Purchasing',
    items: [
      { path: '/products',        label: 'Products',        icon: Package,   perm: 'products.view',        offlineOk: 'readonly' },
      { path: '/inventory',       label: 'Inventory',       icon: Warehouse, perm: 'inventory.view',       offlineOk: 'readonly' },
      { path: '/slow-moving-stock', label: 'Slow-Moving Stock', icon: AlertTriangle, perm: 'inventory.view' },
      { path: '/pending-releases', label: 'Pending Releases', icon: Package, perm: 'inventory.view' },
      { path: '/purchase-orders', label: 'Purchase Orders', icon: Truck,     perm: 'purchase_orders.view', featureFlag: 'purchase_orders' },
      { path: '/pay-supplier',    label: 'Pay Supplier',    icon: Building2, perm: 'purchase_orders.view', featureFlag: 'purchase_orders' },
      { path: '/suppliers',       label: 'Suppliers',       icon: Truck,     perm: 'suppliers.view',       featureFlag: 'supplier_management' },
      { path: '/barcode-print',   label: 'Print Barcodes',  icon: ScanBarcode, perm: 'products.view' },
      { path: '/barcode-manage',  label: 'Barcode Manager', icon: ScanBarcode, perm: 'products.edit' },
    ],
  },
  {
    label: 'Branches',
    items: [
      { path: '/branches',         label: 'Branches',         icon: Building2, perm: 'branches.view' },
      { path: '/stock-requests',   label: 'Stock Requests',   icon: ClipboardList, perm: 'branch_transfers.view', featureFlag: 'branch_transfers' },
      { path: '/branch-transfers', label: 'Branch Transfers', icon: ArrowRight, perm: 'branch_transfers.view', featureFlag: 'branch_transfers' },
      { path: '/internal-invoices', label: 'Internal Invoices', icon: FileText, perm: 'branch_transfers.view', featureFlag: 'branch_transfers' },
      { path: '/branch-pickups',   label: 'Branch Pickups',   icon: GitMerge, perm: 'branch_pickups.view', featureFlag: 'branch_transfers' },
      { path: '/cross-branch-payments', label: 'Cross-Branch Payments', icon: ArrowRight, perm: 'cross_branch_payments.view', featureFlag: 'branch_transfers' },
      { path: '/branch-position', label: 'Branch Position', icon: Scale, perm: 'cross_branch_payments.view', featureFlag: 'branch_transfers' },
    ],
  },
  {
    label: 'End of Day',
    items: [
      { path: '/fund-management', label: 'Fund Management',  icon: Calculator, perm: 'accounting.manage_funds', featureFlag: 'full_fund_management' },
      { path: '/count-sheets',    label: 'Count Sheets',     icon: ClipboardList, perm: 'count_sheets.view' },
      { path: '/daily-ops',       label: 'Daily Operations', icon: Receipt,    perm: 'reports.view' },
      { path: '/shifts',          label: 'Cashier Shifts',   icon: ClipboardList, perm: 'reports.view', shiftsFeatureGated: true },
      { path: '/close-wizard',    label: 'Close Wizard',     icon: Lock,       perm: 'reports.close_day' },
    ],
  },
  {
    label: 'Reports & Audit',
    items: [
      { path: '/reports',          label: 'Reports',          icon: BarChart3,    perm: 'reports.view',  featureFlag: 'advanced_reports' },
      { path: '/audit',            label: 'Audit Center',     icon: ShieldCheck,  perm: 'reports.view',  featureFlag: 'audit_center' },
      { path: '/corrections',      label: 'Correction Center', icon: FilePenLine, perm: 'reports.view' },
      { path: '/incident-tickets', label: 'Incident Tickets', icon: AlertTriangle, perm: 'reports.view' },
      { path: '/bir-compliance',   label: 'BIR Compliance',   icon: FileCheck,    perm: 'reports.view',  adminOnly: true },
      { path: '/accounting',       label: 'Accounting',       icon: Calculator,   perm: 'accounting.view' },
      { path: '/journal-entries',  label: 'Journal Entries',  icon: BookOpen,     perm: 'accounting.view' },
    ],
  },
  {
    label: 'Management',
    items: [
      { path: '/documents',      label: 'Documents',    icon: FolderOpen, perm: null },
      { path: '/employees',       label: 'Employees',    icon: Briefcase, perm: 'settings.manage_users',       featureFlag: 'employee_management' },
      { path: '/price-schemes',   label: 'Price Schemes', icon: Tags,     perm: 'price_schemes.view' },
      { path: '/price-manager',   label: 'Price Manager', icon: TrendingUp, perm: 'products.edit' },
      { path: '/capital-change-report', label: 'Capital Changes', icon: BarChart3, perm: 'products.edit' },
      { path: '/team',            label: 'Team',         icon: UserCog,   perm: 'settings.manage_users' },
      { path: '/import',          label: 'Import Center', icon: Upload,   perm: 'products.create', adminOnly: true },
      { path: '/downloads',       label: 'App Downloads', icon: Smartphone, perm: null, adminOnly: true },
      { path: '/backups',         label: 'Backups',      icon: HardDrive, perm: 'settings.edit' },
      { path: '/settings',        label: 'Settings',     icon: Settings },
    ],
  },
  {
    label: 'Print Center',
    items: [
      { path: '/print-center', label: 'Print Queue', icon: Printer, perm: null, adminOnly: true },
    ],
  },
];

// Flat list used for offline-banner path lookup
const NAV_ITEMS = NAV_SECTIONS.flatMap(s => s.items);

export default function Layout({ children }) {
  const { 
    user, logout, branches, switchBranch, hasPerm, delegations,
    selectedBranchId, canViewAllBranches, viewingBranchName, isConsolidatedView,
    assignedBranchIds,
  } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const isOnline = useOnlineStatus();

  // Register global soft-lock (402) toast handler
  useEffect(() => {
    window.showSoftLockToast = (msg) => {
      toast.error(msg || 'Subscription expired — view-only mode active.', {
        duration: 8000,
        action: {
          label: 'Renew Now →',
          onClick: () => navigate('/upgrade'),
        },
      });
    };
    return () => { delete window.showSoftLockToast; };
  }, [navigate]);

  // "Show locked features" ghost mode — persisted per session
  const [showLockedFeatures, setShowLockedFeatures] = useState(
    () => localStorage.getItem('agri_show_locked') === 'true'
  );
  const [upgradeTeaser, setUpgradeTeaser] = useState(null); // { label, featureFlag }

  const toggleShowLocked = () => {
    const next = !showLockedFeatures;
    setShowLockedFeatures(next);
    localStorage.setItem('agri_show_locked', String(next));
  };

  // Background SMS unread badge — polls every 60s so the nav badge stays fresh
  // even when the user is on Sales, Inventory, etc.
  const [smsPending, setSmsPending] = useState(0);
  const smsPollRef = useRef(null);
  useEffect(() => {
    if (!user || user.role !== 'admin') return;
    const fetchStats = () => {
      // Interceptor auto-appends branch_id when a specific branch is selected
      api.get('/sms/stats').then(res => setSmsPending(res.data.unread || 0)).catch(() => {});
    };
    fetchStats();
    smsPollRef.current = setInterval(fetchStats, 60000);
    return () => clearInterval(smsPollRef.current);
  }, [user?.role]); // eslint-disable-line

  // Section override dialog state
  const [overrideDialog, setOverrideDialog] = useState({ open: false, module: '', label: '', path: '' });

  // Multi-Cashier Shifts feature flag (drives visibility of /shifts nav item).
  // Polled once on mount and again on branch switch — the badge polls
  // separately for finer cadence.
  const [shiftsEnabled, setShiftsEnabled] = useState(false);
  useEffect(() => {
    let cancelled = false;
    api.get('/shifts/feature-status').then((res) => {
      if (!cancelled) setShiftsEnabled(!!res.data?.enabled);
    }).catch(() => { /* feature unavailable */ });
    return () => { cancelled = true; };
  }, [user?.organization_id]);

  const getItemPermStatus = (item) => {
    // Check subscription feature flag first
    if (item.featureFlag && user && !user.is_super_admin) {
      const features = user?.subscription?.features;
      if (features && features[item.featureFlag] === false) return 'feature_locked';
    }
    // Check permission
    if (item.perm) {
      const [mod, act] = item.perm.split('.');
      if (!hasPerm(mod, act)) return 'perm_locked';
    }
    return 'allowed';
  };

  // Build nav sections: locked items hidden by default, shown as ghosts when showLockedFeatures
  const allSections = NAV_SECTIONS.map(section => ({
    ...section,
    items: section.items.map(item => ({
      ...item,
      _status: getItemPermStatus(item),
    })).filter(item => {
      if (item._status === 'feature_locked' && !showLockedFeatures) return false;
      if (item.adminOnly && user?.role !== 'admin' && user?.role !== 'owner') return false;
      if (item.shiftsFeatureGated && !shiftsEnabled) return false;
      return true;
    }),
  })).filter(section => section.items.length > 0);

  const NavLink = ({ item }) => {
    const active = location.pathname === item.path;
    const offlineLocked = !isOnline && !item.offlineOk;
    const readOnly = !isOnline && item.offlineOk === 'readonly';
    const permLocked = item._status === 'perm_locked';
    const featureLocked = item._status === 'feature_locked';

    const handleClick = (e) => {
      if (offlineLocked) {
        e.preventDefault();
        toast.error('You\'re offline — this area requires internet', { duration: 2500 });
        return;
      }
      if (permLocked) {
        e.preventDefault();
        const [mod] = (item.perm || '').split('.');
        setOverrideDialog({ open: true, module: mod, label: item.label, path: item.path });
        return;
      }
      if (featureLocked) {
        e.preventDefault();
        setUpgradeTeaser({ label: item.label, featureFlag: item.featureFlag });
        return;
      }
      setSidebarOpen(false);
    };

    return (
      <Link
        to={featureLocked || permLocked ? '#' : item.path}
        data-testid={`nav-${item.path.slice(1)}`}
        onClick={handleClick}
        className={`relative flex items-center gap-3 px-4 py-2 rounded-md text-[13px] font-medium transition-colors duration-75 ${
          featureLocked
            ? 'text-slate-600 italic opacity-60 hover:opacity-80 hover:text-slate-400 cursor-pointer'
            : offlineLocked
            ? 'text-slate-600 opacity-50 cursor-not-allowed'
            : permLocked
            ? 'text-slate-500 hover:text-slate-300 hover:bg-white/[0.04] cursor-pointer'
            : active
            ? 'bg-white/[0.06] text-white'
            : 'text-slate-400 hover:text-white hover:bg-white/[0.04]'
        }`}
      >
        {active && !featureLocked && (
          <span className="absolute left-0 top-1.5 bottom-1.5 w-[2px] bg-[#22C55E] rounded-r" />
        )}
        {featureLocked
          ? <Lock size={15} strokeWidth={1.5} className="opacity-40 shrink-0" />
          : <item.icon size={17} strokeWidth={1.75} />
        }
        <span className="flex-1">{item.label}</span>
        {/* SMS unread badge */}
        {item.path === '/messages' && smsPending > 0 && !featureLocked && (
          <span className="bg-red-500 text-white text-[9px] font-bold rounded-full px-1.5 py-0.5 min-w-[18px] text-center">
            {smsPending}
          </span>
        )}
        {featureLocked && (
          <span className="text-[9px] font-bold uppercase tracking-wider text-amber-600/70 shrink-0">Upgrade</span>
        )}
        {offlineLocked && <WifiOff size={11} className="text-slate-500 shrink-0" />}
        {permLocked && <Lock size={11} className="text-amber-500/70 shrink-0" />}
        {readOnly && !active && <span className="text-[9px] text-slate-500 shrink-0">cached</span>}
      </Link>
    );
  };

  const SidebarContent = () => (
    <div className="flex flex-col h-full">
      <div className="px-5 py-5 flex items-center gap-3">
        <div className="w-9 h-9 rounded-lg bg-[#1A4D2E] flex items-center justify-center">
          <Store size={18} className="text-white" strokeWidth={2} />
        </div>
        <div>
          <h1 className="text-base font-bold text-white tracking-tight" style={{ fontFamily: 'Manrope' }}>AgriBooks</h1>
          <p className="text-[11px] text-slate-500">Business Management</p>
        </div>
      </div>
      <Separator className="bg-white/5" />
      <div className="px-3 py-3">
        {canViewAllBranches ? (
          <Select
            value={selectedBranchId}
            onValueChange={(val) => switchBranch(val)}
          >
            <SelectTrigger
              data-testid="branch-selector"
              className="bg-white/5 border-white/10 text-white text-xs h-9"
            >
              <div className="flex items-center gap-2">
                {isConsolidatedView && <Building2 size={14} className="text-emerald-400" />}
                <SelectValue placeholder="Select Branch">{viewingBranchName}</SelectValue>
              </div>
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">
                <div className="flex items-center gap-2">
                  <Building2 size={14} className="text-emerald-500" />
                  <span className="font-medium">All Branches</span>
                  <span className="text-xs text-slate-400">(Consolidated)</span>
                </div>
              </SelectItem>
              <Separator className="my-1" />
              {/* Multi-branch managers/auditors only see their assigned
                  branches; admins (and legacy unscoped users) see all. */}
              {(() => {
                const visible = (user?.role === 'admin' || assignedBranchIds.length === 0)
                  ? branches
                  : branches.filter(b => assignedBranchIds.includes(b.id));
                return visible.map(b => (
                  <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>
                ));
              })()}
            </SelectContent>
          </Select>
        ) : (
          <div className="bg-white/5 border border-white/10 rounded-md px-3 py-2 text-xs text-white flex items-center gap-2">
            <Building2 size={14} className="text-slate-400" />
            <span>{viewingBranchName}</span>
            <span className="ml-auto text-slate-500">🔒</span>
          </div>
        )}
      </div>
      <ScrollArea className="flex-1 px-3">
        <nav className="py-2">
          {allSections.map((section, si) => (
            <div key={section.label || 'top'} className={si > 0 ? 'mt-1' : ''}>
              {section.label && (
                <div className={`flex items-center gap-2 px-2 ${si > 0 ? 'pt-4' : 'pt-2'} pb-1.5`}>
                  <span className="text-[10px] font-semibold uppercase tracking-widest text-slate-600">
                    {section.label}
                  </span>
                  <div className="flex-1 h-px bg-white/5" />
                </div>
              )}
              <div className="space-y-0.5">
                {section.items.map(item => <NavLink key={item.path} item={item} />)}
              </div>
            </div>
          ))}
        </nav>
      </ScrollArea>
      <Separator className="bg-white/5" />
      {/* Show locked features toggle */}
      <div className="px-3 py-2">
        <button
          onClick={toggleShowLocked}
          data-testid="show-locked-features-toggle"
          className={`w-full flex items-center gap-2 px-3 py-1.5 rounded-md text-[11px] font-medium transition-colors ${
            showLockedFeatures
              ? 'bg-amber-500/15 text-amber-400 hover:bg-amber-500/20'
              : 'text-slate-600 hover:text-slate-400 hover:bg-white/[0.03]'
          }`}
        >
          {showLockedFeatures ? <EyeOff size={12} /> : <Eye size={12} />}
          {showLockedFeatures ? 'Hide locked features' : 'Show locked features'}
          {!showLockedFeatures && <Zap size={10} className="ml-auto text-amber-500/50" />}
        </button>
      </div>
      <Separator className="bg-white/5" />
      <OfflineIndicator />
      <Separator className="bg-white/5" />
      <div className="p-3">
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button
              data-testid="user-menu-trigger"
              className="w-full flex items-center gap-3 px-3 py-2.5 rounded-md hover:bg-white/5 transition-colors"
            >
              <div className="w-8 h-8 rounded-full bg-[#1A4D2E] flex items-center justify-center text-white text-xs font-bold">
                {user?.full_name?.[0] || user?.username?.[0] || 'U'}
              </div>
              <div className="flex-1 text-left">
                <p className="text-sm text-white font-medium truncate">{user?.full_name || user?.username}</p>
                <p className="text-[11px] text-slate-500 capitalize">{user?.role}</p>
              </div>
              <ChevronDown size={14} className="text-slate-500" />
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-48">
            <DropdownMenuItem onClick={() => navigate('/settings')}>
              <User size={14} className="mr-2" /> Profile
            </DropdownMenuItem>
            {!user?.is_super_admin && (
              <DropdownMenuItem onClick={() => navigate('/upgrade')}>
                <BarChart3 size={14} className="mr-2" /> Upgrade Plan
              </DropdownMenuItem>
            )}
            {!user?.is_super_admin && (
              <DropdownMenuItem onClick={() => navigate('/features')}>
                <Zap size={14} className="mr-2" /> My Features
              </DropdownMenuItem>
            )}
            {user?.is_super_admin && (
              <DropdownMenuItem onClick={() => navigate('/superadmin')}>
                <Shield size={14} className="mr-2" /> Platform Admin
              </DropdownMenuItem>
            )}
            <DropdownMenuSeparator />
            <DropdownMenuItem data-testid="logout-btn" onClick={logout} className="text-red-600">
              <LogOut size={14} className="mr-2" /> Logout
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </div>
  );

  return (
    <div className="flex h-screen overflow-hidden">
      {/* Desktop Sidebar */}
      <aside className="hidden lg:flex w-[250px] flex-col bg-[#0F172A] sidebar-texture shrink-0">
        {SidebarContent()}
      </aside>
      {/* Mobile Sidebar Overlay */}
      {sidebarOpen && (
        <div className="fixed inset-0 z-50 lg:hidden">
          <div className="absolute inset-0 bg-black/60" onClick={() => setSidebarOpen(false)} />
          <aside className="absolute left-0 top-0 bottom-0 w-[250px] bg-[#0F172A] sidebar-texture">
            <button onClick={() => setSidebarOpen(false)} className="absolute top-4 right-4 text-white">
              <X size={20} />
            </button>
            {SidebarContent()}
          </aside>
        </div>
      )}
      {/* Main Content */}
      <main className="flex-1 flex flex-col overflow-hidden bg-[#F5F5F0]">
        <header className="h-14 border-b border-slate-200 bg-white flex items-center justify-between px-4 lg:px-6 shrink-0">
          <div className="flex items-center gap-3">
            <button data-testid="mobile-menu-btn" onClick={() => setSidebarOpen(true)} className="lg:hidden p-1">
              <Menu size={20} />
            </button>
            <div className="flex items-center gap-2">
              {isConsolidatedView ? (
                <div className="flex items-center gap-2 px-2 py-1 rounded-md bg-emerald-50 border border-emerald-200">
                  <Building2 size={14} className="text-emerald-600" />
                  <span className="text-sm font-medium text-emerald-700">All Branches</span>
                </div>
              ) : (
                <h2 className="text-sm font-semibold text-slate-700" style={{ fontFamily: 'Manrope' }}>
                  {viewingBranchName}
                </h2>
              )}
            </div>
          </div>
          <div className="flex items-center gap-2 text-xs text-slate-500">
            <ActiveShiftBadge branchId={selectedBranchId} />
            <QuickSearch />
            <NotificationBell />
            <span className="hidden sm:inline">{user?.full_name || user?.username}</span>
            <span className="capitalize bg-slate-100 px-2 py-0.5 rounded text-[11px]">{user?.role}</span>
          </div>
        </header>
        {/* Demo account banner */}
        <DemoBanner />
        {/* Offline banner — only on pages that require internet */}
        {!isOnline && (() => {
          const currentItem = NAV_ITEMS.find(i => i.path === location.pathname);
          const isLocked = !currentItem?.offlineOk;
          if (!isLocked) return null;
          return (
            <div
              className="shrink-0 flex items-center gap-3 px-4 py-2.5 border-b"
              style={{ backgroundColor: 'var(--warning-tint)', borderColor: 'var(--warning-bg)' }}
            >
              <span
                className="inline-flex items-center justify-center w-7 h-7 rounded-md flex-shrink-0"
                style={{ backgroundColor: 'var(--warning-bg)' }}
              >
                <WifiOff size={13} style={{ color: 'var(--warning-icon)' }} />
              </span>
              <span className="text-[13px] font-medium" style={{ color: 'var(--warning-text)' }}>
                You&apos;re offline — this page requires internet. Data shown may be outdated.
                Switch to <strong>Sales</strong> for offline operations.
              </span>
            </div>
          );
        })()}
        {/* Trial expiry banner */}
        {user?.subscription && (() => {
          const sub = user.subscription;
          const effective = sub.effective_plan;
          const grace = sub.grace_info;

          // Grace period banner (highest priority)
          if (effective === 'grace_period' && grace?.in_grace) {
            const urgency = grace.days_left <= 1;
            return (
              <div
                className="shrink-0 flex items-center justify-between gap-3 px-4 py-2.5 border-b"
                style={{
                  backgroundColor: urgency ? 'var(--danger-tint)' : 'var(--warning-tint)',
                  borderColor:     urgency ? 'var(--danger-bg)'   : 'var(--warning-bg)',
                }}
              >
                <span className="text-[13px] font-medium" style={{ color: urgency ? 'var(--danger-text)' : 'var(--warning-text)' }}>
                  {grace.days_left === 0
                    ? 'Your account locks TODAY. '
                    : `Access locks in ${grace.days_left} day${grace.days_left !== 1 ? 's' : ''} (${grace.locked_at}). `}
                  <strong>Renew your subscription to keep access.</strong>
                </span>
                <button
                  onClick={() => navigate('/upgrade')}
                  className="text-[12px] font-semibold px-3 h-7 rounded-md flex-shrink-0 text-white transition-colors duration-75"
                  style={{ backgroundColor: urgency ? 'var(--danger-icon)' : 'var(--warning-icon)' }}
                >
                  Renew Now
                </button>
              </div>
            );
          }

          if (effective === 'expired') {
            return (
              <div
                className="shrink-0 flex items-center justify-between gap-3 px-4 py-2.5 border-b"
                style={{ backgroundColor: 'var(--danger-tint)', borderColor: 'var(--danger-bg)' }}
              >
                <span className="text-[13px] font-medium" style={{ color: 'var(--danger-text)' }}>
                  Your subscription has expired. Access to most features is locked.
                </span>
                <button
                  onClick={() => navigate('/upgrade')}
                  className="text-[12px] font-semibold px-3 h-7 rounded-md flex-shrink-0 text-white transition-colors duration-75"
                  style={{ backgroundColor: 'var(--danger-icon)' }}
                >
                  Reactivate
                </button>
              </div>
            );
          }

          if (sub.plan === 'trial') {
            const daysLeft = sub.trial_ends_at
              ? Math.ceil((new Date(sub.trial_ends_at) - new Date()) / 86400000)
              : null;
            if (!daysLeft || daysLeft > 5) return null;
            return (
              <div
                className="shrink-0 flex items-center justify-between gap-3 px-4 py-2.5 border-b"
                style={{ backgroundColor: 'var(--warning-tint)', borderColor: 'var(--warning-bg)' }}
              >
                <span className="text-[13px] font-medium" style={{ color: 'var(--warning-text)' }}>
                  Trial ends in <strong>{daysLeft} day{daysLeft !== 1 ? 's' : ''}</strong>. Upgrade to keep access.
                </span>
                <button
                  onClick={() => navigate('/upgrade')}
                  className="text-[12px] font-semibold px-3 h-7 rounded-md flex-shrink-0 text-white transition-colors duration-75"
                  style={{ backgroundColor: 'var(--warning-icon)' }}
                >
                  Upgrade Now
                </button>
              </div>
            );
          }

          return null;
        })()}
        <div className="flex-1 overflow-auto p-4 lg:p-6">
          {children}
        </div>
      </main>

      {/* Section Override Dialog */}
      <SectionOverrideDialog
        open={overrideDialog.open}
        onOpenChange={(open) => setOverrideDialog(prev => ({ ...prev, open }))}
        module={overrideDialog.module}
        moduleLabel={overrideDialog.label}
        onGranted={() => {
          navigate(overrideDialog.path);
          setSidebarOpen(false);
        }}
      />

      {/* Feature Preview / Upgrade Teaser Modal (for ghost/locked feature items) */}
      {upgradeTeaser && (
        <FeaturePreviewModal
          featureFlag={upgradeTeaser.featureFlag}
          label={upgradeTeaser.label}
          onClose={() => setUpgradeTeaser(null)}
        />
      )}

      {/* Onboarding Wizard — shown to new users on first login */}
      <OnboardingWizard />

      {/* Branch Transfer Alert Bubble — snoozable, fully hidden when snoozed */}
      <BranchTransferAlertBubble />
    </div>
  );
}
